import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback, useState } from "react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { TextRenderer } from "../RichText/TextRenderer";
import { defaultStyles } from "./defaultStyles";

interface ButtonStylesSet {
  default: Record<string, StyleValue>;
  hover: Record<string, StyleValue>;
  margin: StyleValue;
  padding: StyleValue;
  sizing: Record<string, StyleValue>;
}

interface ButtonProps extends ComponentPropsWithRef<"a"> {
  blockPath: string;
  "data-preview-mode": boolean;
  href: string;
  styles: ButtonStylesSet;
  text: string;
}

export const PrimaryButton = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      blockPath,
      text,
      href,
      styles,
      className,
      onClick,
      target = "_self",
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const currentStyles = isHovered ? styles.hover : styles.default;
    const inlineStyles = {
      ...parseStyles({
        styles: currentStyles,
        defaultStyles: isHovered ? defaultStyles.hover : defaultStyles.default,
      }),
      ...parseStyles({
        styles: styles.sizing,
        defaultStyles: defaultStyles.sizing,
      }),
      ...parseStyles({
        styles: { margin: styles.margin },
        defaultStyles: { margin: defaultStyles.margin },
      }),
      ...parseStyles({
        styles: { padding: styles.padding },
        defaultStyles: { padding: defaultStyles.padding },
      }),
    };

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreviewMode) event.preventDefault();
        onClick?.(event);
      },
      [isPreviewMode, onClick],
    );

    return (
      <a
        ref={ref}
        className={classNames(
          "wb-inline-flex",
          "wb-min-h-fit wb-min-w-fit wb-items-center wb-justify-center wb-transition-colors",
          "wb-pt-button-primary-top wb-pb-button-primary-bottom wb-pl-button-primary-left wb-pr-button-primary-right",
          "wb-text-button-primary wb-font-button-primary wb-leading-button-primary wb-tracking-button-primary wb-text-center",
          "wb-bg-button-primary wb-text-button-primary-text wb-border-button-primary",
          "wb-rounded-tl-button-primary-default-left-top wb-rounded-tr-button-primary-default-right-top wb-rounded-bl-button-primary-default-left-bottom wb-rounded-br-button-primary-default-right-bottom",
          "wb-shadow-button-primary",
          "hover:wb-bg-button-primary-hover hover:wb-text-button-primary-text-hover",
          "hover:wb-border-button-primary-hover",
          "hover:wb-rounded-tl-button-primary-hover-left-top hover:wb-rounded-tr-button-primary-hover-right-top hover:wb-rounded-bl-button-primary-hover-left-bottom hover:wb-rounded-br-button-primary-hover-right-bottom",
          "hover:wb-font-button-primary-hover hover:wb-leading-button-primary-hover hover:wb-tracking-button-primary-hover",
          "hover:wb-shadow-button-primary-hover",
          className,
        )}
        style={inlineStyles}
        href={href}
        target={target}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        rel="noreferrer noopener"
        {...rest}
      >
        <TextRenderer blockPath={blockPath} text={text} />
      </a>
    );
  },
);

PrimaryButton.displayName = "PrimaryButton";
